import type { Provider } from '@vgtv/api-client';
import type { Config } from '@schibsted-svp/web-player';

import type {
  AdnContext,
  PlayerConfigOptions,
  PlayerConfigPaywall,
} from '../types/global';
import { areAirplayAdsEnabled } from '../utils/airplayAds';

import getUserId from './adn/getUserId';
import getAdnSettingsVgtv from './adn/vgtv/getAdnSettings';
import getAdnSettingsAb from './adn/ab/getAdnSettings';
import getPlayerToken, { TOKEN_API_HOSTNAME } from './paywall/getPlayerToken';
import * as SPiD from './paywall/spid';
import { defaultConfig, defaultOptions } from './defaults';
import { getPluginUrl, getSkinUrl, getDnaUrl } from './helpers/staticUrls';
import { createPulseDecorator } from './pulse';
import { withPPID } from './adn/ppid';
import { getConsentManager } from './adn/privacy';
import { AutoplayManager } from './adn/ab/AutoplayManager';
import { getAabConfigURL } from './aabConfig';
import { resolveExperiments } from './experiments';
import { countryMap } from './countryMap';
import { vendorToDnaBrand } from './vendorToDnaBrand';

export type SchibstedAccount = typeof SPiD & { env: 'PRE' | 'PRO' | 'PRO_NO' };

export default function getPlayerConfig(
  options: Partial<PlayerConfigOptions>,
  config: Config
): Config {
  options = {
    ...defaultOptions,
    ...options,
    plugins: {
      ...defaultOptions.plugins,
      ...options.plugins,
    },
    ads: {
      ...defaultOptions.ads,
      ...options.ads,
    },
  };

  const vendor = (config.vendor || defaultConfig.vendor) as Provider;

  const providerCountry = Object.keys(countryMap).find((k) =>
    countryMap[k].includes(vendor)
  );

  const dnaBrand = vendorToDnaBrand[vendor];
  const dnaLinkId = `player-dna-${dnaBrand}`;
  if (dnaBrand && !document.getElementById(dnaLinkId)) {
    const link = document.createElement('link');
    link.id = dnaLinkId;
    link.rel = 'stylesheet';
    link.href = getDnaUrl(dnaBrand);
    document.head.appendChild(link);
  }

  const experiments = options.experiments ?? [];
  if (providerCountry === 'no') {
    experiments.push({ id: 'vmap', variant: 'enabled' } as any);
  }

  const experimentMap = experiments.reduce(
    (aggr, { id, variant }) => ({ ...aggr, [id]: variant }),
    {} as Record<string, string>
  );

  const settings: Config['settings'] = {
    ...defaultConfig.settings,
    ...config.settings,
    jw: { ...defaultConfig?.settings?.jw, ...config?.settings?.jw },
  };

  const paywallAccess: PlayerConfigPaywall = {
    ...defaultOptions.paywallAccess!,
    ...options.paywallAccess,
    message: {
      ...defaultOptions.paywallAccess?.message,
      ...options.paywallAccess?.message,
    },
  };

  if (['vgtv', 'audio'].includes(config.skin?.name!)) {
    config.skin = {
      name: 'svp',
      url: getSkinUrl('svp'),
    };
  } else if (['vgtv', 'e24'].includes(vendor)) {
    config.skin = defaultConfig.skin;
  } else {
    config.skin = {
      name: 'svp',
      url: getSkinUrl('svp'),
      ...config.skin,
    };
  }

  const configOverrides: Partial<Config> & {
    disableCountdownPlugin?: boolean;
  } = {};

  if (TOKEN_API_HOSTNAME[vendor]) {
    configOverrides.tokenV2Url =
      TOKEN_API_HOSTNAME[vendor]['pro'].concat('v2/');
  }

  const consentManager = getConsentManager(vendor);
  const adnContext: AdnContext = {
    consentManager,
    autoplayManager:
      providerCountry === 'se' ? new AutoplayManager() : undefined,
    identityHelper: SPiD,
  };

  if (experimentMap.vmap === 'enabled') {
    settings.vmap = async () =>
      adnContext.adnPlugin?.getVmap({
        site: options.site!,
        experimentMap,
        consentManager,
      });
  }

  let plugins: Config['plugins'] = {};

  if (config.plugins) {
    plugins = { ...config.plugins };
  } else if (options.plugins?.mediaSession) {
    plugins[getPluginUrl('media-session')] = {
      name: 'MediaSessionPlugin',
    };
  }

  if (providerCountry === 'no') {
    plugins[getPluginUrl('adn')] = {
      name: 'AdnPlugin',
      options: {
        options,
        context: adnContext,
      },
    };
  }

  if (config.skin?.name === 'vgtv2') {
    let schibstedAccountEnv: 'PRE' | 'PRO' | 'PRO_NO';

    if (paywallAccess.usePre) {
      schibstedAccountEnv = 'PRE';
    } else {
      schibstedAccountEnv = providerCountry === 'no' ? 'PRO_NO' : 'PRO';
    }

    const recommended: Config['recommended'] =
      typeof config.recommended === 'boolean'
        ? {}
        : {
            ...defaultConfig.recommended,
            ...config.recommended,
          };

    const recommendedAutoplay = Boolean(
      recommended.autoplay || recommended.grid || recommended.next
    );

    plugins[getPluginUrl('skin-vgtv2')] = {
      name: 'VgtvSkinPlugin',
      options: {
        schibstedAccount: { ...SPiD, env: schibstedAccountEnv },
        context: options.context ?? 'embed',
        playlist: options.playlist,
        ui: options.ui,
        experiments,
        recommended: {
          autoplay: recommendedAutoplay,
        },
        utmParameters: paywallAccess.message?.utmParameters,
        onLoginClick:
          paywallAccess.message?.onLoginClick ||
          (() => {
            if (paywallAccess.message?.onRedirect instanceof Function) {
              paywallAccess.message.onRedirect();
            }

            SPiD.login();
          }),
      },
    };

    SPiD.initIdentity(paywallAccess.getSpidIdentityInstance);
    SPiD.initMonetization(
      paywallAccess.getSpidMonetizationInstance,
      vendor,
      schibstedAccountEnv
    );

    Object.assign(settings.jw!, {
      controls: false,
      displaytitle: false,
      fullscreenOrientationLock: 'none',
      plugins: {
        [getPluginUrl('chromecast')]: {
          vendor,
          site: options.site,
          externalUid: getUserId(),
          consentManager,
          getUserAccesses: SPiD.getUserAccesses,
        },
      },
    });

    configOverrides.recommended = false;
    configOverrides.disableCountdownPlugin = true;
  }

  if (
    options.plugins?.topicSubscriber &&
    vendor === 'vgtv' &&
    paywallAccess.getSpidIdentityInstance
  ) {
    plugins[getPluginUrl('topic-subscriber')] = {
      name: 'TopicSubscriberPlugin',
      options: { getUserSettings: SPiD.getUserSettings, consentManager },
    };

    SPiD.initIdentity(paywallAccess.getSpidIdentityInstance);
  }

  if (options.plugins?.sponsorOverlay) {
    plugins[getPluginUrl('sponsor-overlay')] = {
      name: 'SponsorOverlayPlugin',
    };
  }

  if (options.plugins?.sifoTracking) {
    plugins[getPluginUrl('sifo-tracking')] = {
      name: 'SifoTracking',
      options: options.plugins.sifoTracking as unknown as Record<
        string,
        unknown
      >,
    };
  }

  // Skipped when `sponsorOverlay` is enabled - that plugin already fires
  // the same set of pixel trackers internally.
  if (
    options.plugins?.trackViewPercentage &&
    !options.plugins?.sponsorOverlay
  ) {
    plugins[getPluginUrl('track-view-percentage')] = {
      name: 'TrackViewPercentage',
    };
  }

  if (options.plugins?.displayCoverTag) {
    plugins[getPluginUrl('display-cover-tag')] = {
      name: 'DisplayCoverTag',
      options: options.plugins.displayCoverTag as unknown as Record<
        string,
        unknown
      >,
    };
  }

  if (
    (vendor === 'ab' && config.skin?.name !== 'vgtv2') ||
    (config.skin?.name === 'vgtv2' &&
      // chromecast vs airplay
      (window.chrome || areAirplayAdsEnabled()))
  ) {
    // @ts-expect-error this is fine
    settings.jw.cast = undefined;
  }

  resolveExperiments(vendor);

  return {
    ...defaultConfig,
    ...config,
    sharing: false,
    settings,
    plugins,
    recommended: {
      ...defaultConfig?.recommended,
      ...(typeof config.recommended === 'boolean' ? {} : config.recommended),
    },
    pulse: {
      ...defaultConfig.pulse,
      ...config.pulse,
      tpaas: experimentMap.tpaas === 'enabled',
      decorator: createPulseDecorator(
        vendor,
        // PulseProvider handles it for vgtv2. Legacy player only
        Boolean(options.paywallAccess && config.skin?.name !== 'vgtv2'),
        adnContext,
        SPiD.getUserIdAsync,
        config.pulse?.decorator
      ),
      sdkConfig: {
        requireAdvertisingOptIn:
          consentManager.type === 'tcf' ? true : undefined,
        consents: consentManager.getConsentsForPulse(),
      },
    },
    ...(vendor === 'vgtv'
      ? {
          aab: 'auto',
          aabConfig: getAabConfigURL(),
        }
      : undefined),
    adn: withPPID(
      providerCountry === 'se' ? getAdnSettingsAb : getAdnSettingsVgtv
    ).bind(null, adnContext, options) as Config['adn'],
    token: options.paywallAccess?.enabled
      ? (getPlayerToken(paywallAccess, vendor) as typeof config.token)
      : config.token,

    ...configOverrides,
  };
}
